/* eslint-disable react/no-unescaped-entities */
/**
 * /trouver-un-avocat, /trouver-un-expert-comptable, /trouver-un-notaire,
 * /trouver-un-mediateur, /trouver-un-commissaire-de-justice, /réseau de spécialistes-spécialistes
 *
 * Page SEO institutionnelle générique pour les pages « Trouver un X ».
 * Configurable via TROUVER_PAGES (sync avec /app/backend/seo_routes.py).
 *
 * Variante locale : /trouver-un-avocat/:city (TOP_CITIES_FR).
 */
import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ShieldCheck, ArrowRight, Compass, Users, MapPin, ScanLine, GitMerge,
  Scale, BookOpen, BadgeCheck, FileSearch,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Seo, { ldBreadcrumb, ldFAQ } from "@/components/Seo";
import { apiClient } from "@/lib/api";


// ----------------------------------------------------------------------------
// Configuration synchronisée avec /app/backend/seo_routes.py · TROUVER_PAGES
// ----------------------------------------------------------------------------
export const TROUVER_PAGES = {
  "trouver-un-avocat": {
    label: "avocat",
    plural: "avocats",
    h1: "Trouver un avocat qualifié pour votre dossier",
    tagline: "Orientation institutionnelle vers des avocats sélectionnés.",
    seo_title: "Trouver un avocat qualifié — Orientation CONFILEX",
    seo_description: "CONFILEX vous aide à identifier un avocat compétent pour votre dossier. Orientation institutionnelle, qualification documentaire préalable et mise en relation sécurisée sur tout le territoire français.",
    profession_slug: "avocat",
    intent_keywords: ["trouver un avocat", "avocat", "consultation avocat", "avocat compétent"],
    service_areas: [
      "Droit des affaires", "Droit immobilier", "Droit du travail",
      "Droit de la famille", "Droit pénal", "Droit fiscal",
      "Contentieux civil", "Droit des sociétés",
    ],
    faq: [
      { q: "Comment CONFILEX choisit-il les avocats référencés ?", a: "CONFILEX référence des avocats inscrits à un Barreau français en activité, après qualification documentaire et vérification d'inscription. Aucun classement, aucune notation publique, aucune commission perçue sur leurs honoraires." },
      { q: "Quel est le coût pour trouver un avocat ?", a: "L'orientation initiale est gratuite. CONFILEX propose une Mission de Recherche Premium d'Spécialiste à 89 € TTC lorsque vous souhaitez une qualification documentaire approfondie et la transmission de votre dossier à au moins 4 profils potentiellement pertinents." },
      { q: "CONFILEX donne-t-il un avis juridique ?", a: "Non. CONFILEX est une plateforme d'orientation et d'analyse documentaire. Aucune consultation juridique, aucun avis juridique, aucune recommandation contentieuse n'est délivré par la plateforme." },
    ],
    related: ["trouver-un-expert-comptable", "trouver-un-notaire", "réseau de spécialistes-spécialistes"],
  },
  "trouver-un-expert-comptable": {
    label: "expert-comptable",
    plural: "experts-comptables",
    h1: "Trouver un expert-comptable adapté à votre activité",
    tagline: "Orientation vers des experts-comptables référencés CONFILEX.",
    seo_title: "Trouver un expert-comptable — Orientation CONFILEX",
    seo_description: "Identifiez un expert-comptable adapté à votre activité : création, tenue, révision, audit. Mise en relation sécurisée sur tout le territoire français.",
    profession_slug: "expert-comptable",
    intent_keywords: ["trouver un expert-comptable", "expert-comptable", "cabinet d'expertise comptable"],
    service_areas: [
      "Tenue comptable", "Révision des comptes", "Situation intermédiaire",
      "Comptes annuels", "Création d'entreprise", "Optimisation fiscale",
      "Paie & déclarations sociales", "Audit légal & contractuel",
    ],
    faq: [
      { q: "Comment trouver un expert-comptable compétent ?", a: "CONFILEX qualifie d'abord votre besoin (création, tenue, révision, situation, audit) puis propose des profils d'experts-comptables inscrits à l'Ordre dont la spécialisation déclarée correspond à votre dossier." },
      { q: "Faut-il un expert-comptable pour une TPE ?", a: "Aucune obligation légale en droit français. Cependant, un expert-comptable inscrit à l'Ordre apporte un cadre de tenue conforme et une sécurité dans la production des comptes annuels." },
      { q: "Quels documents préparer avant un rendez-vous ?", a: "Selon votre activité : K-bis ou avis Sirene, statuts, comptes des exercices précédents, déclarations fiscales récentes, conventions ou contrats structurants. Le Vérificateur CONFILEX peut produire une lecture documentaire factuelle de vos pièces." },
    ],
    related: ["trouver-un-avocat", "trouver-un-notaire", "réseau de spécialistes-spécialistes"],
  },
  "trouver-un-notaire": {
    label: "notaire",
    plural: "notaires",
    h1: "Trouver un notaire compétent pour vos actes",
    tagline: "Orientation vers des notaires référencés CONFILEX.",
    seo_title: "Trouver un notaire — Orientation CONFILEX",
    seo_description: "Identifiez un notaire compétent pour vos actes : vente, donation, succession, contrat de mariage, SCI familiale. Mise en relation institutionnelle.",
    profession_slug: "notaire",
    intent_keywords: ["trouver un notaire", "notaire", "notaire succession", "notaire vente"],
    service_areas: [
      "Vente immobilière", "Donation entre vifs", "Succession",
      "Contrat de mariage", "SCI familiale", "Pacte civil de solidarité",
      "Acte authentique de prêt", "Mandat de protection future",
    ],
    faq: [
      { q: "Comment trouver un notaire compétent ?", a: "CONFILEX qualifie d'abord la nature de votre acte (vente, donation, succession, contrat) puis propose des notaires en exercice dont la spécialisation déclarée correspond à votre besoin." },
      { q: "Quels documents préparer avant le rendez-vous notarial ?", a: "Selon l'acte : pièces d'identité, justificatifs de domicile, titres de propriété, comptes de succession, contrats matrimoniaux antérieurs. Le Vérificateur CONFILEX peut produire une lecture documentaire factuelle préalable." },
    ],
    related: ["trouver-un-avocat", "trouver-un-expert-comptable", "réseau de spécialistes-spécialistes"],
  },
  "trouver-un-mediateur": {
    label: "médiateur",
    plural: "médiateurs",
    h1: "Trouver un médiateur pour une résolution amiable",
    tagline: "Orientation vers des médiateurs qualifiés.",
    seo_title: "Trouver un médiateur — Résolution amiable CONFILEX",
    seo_description: "Orientation vers des médiateurs qualifiés pour résoudre amiablement un différend civil, commercial ou familial. Démarche confidentielle.",
    profession_slug: "mediateur",
    intent_keywords: ["trouver un médiateur", "médiation", "résolution amiable"],
    service_areas: [
      "Médiation civile", "Médiation commerciale", "Médiation familiale",
      "Médiation entre associés", "Médiation locataire-bailleur",
      "Médiation contractuelle", "Résolution amiable pré-contentieuse",
    ],
    faq: [
      { q: "À quoi sert un médiateur ?", a: "Le médiateur facilite la résolution amiable d'un différend sans saisir un juge. La démarche est confidentielle, volontaire et structurée. L'accord trouvé peut être homologué." },
      { q: "Médiation ou avocat ?", a: "Ce sont des fonctions complémentaires. Un médiateur intervient en amont du contentieux ; un avocat conseille, rédige et représente. CONFILEX oriente vers la fonction la plus adaptée à votre situation." },
    ],
    related: ["trouver-un-avocat", "trouver-un-commissaire-de-justice"],
  },
  "trouver-un-commissaire-de-justice": {
    label: "commissaire de justice",
    plural: "commissaires de justice",
    h1: "Trouver un commissaire de justice (ex-huissier)",
    tagline: "Constat, signification, recouvrement, exécution.",
    seo_title: "Trouver un commissaire de justice — CONFILEX",
    seo_description: "Identifiez un commissaire de justice pour vos constats, signification d'actes, recouvrement amiable ou exécution forcée.",
    profession_slug: "huissier",
    intent_keywords: ["trouver un commissaire de justice", "huissier", "constat", "recouvrement"],
    service_areas: [
      "Constat probatoire", "Signification d'acte", "Recouvrement amiable",
      "Recouvrement judiciaire", "Exécution forcée", "Procès-verbal de constat",
      "Saisie conservatoire", "Inventaire",
    ],
    faq: [
      { q: "Qu'est-ce qu'un commissaire de justice ?", a: "Le commissaire de justice est la profession issue de la fusion des huissiers de justice et des commissaires-priseurs judiciaires (réforme 2022). Il établit constats, signifie des actes, conduit le recouvrement amiable et exécute les décisions de justice." },
      { q: "Un constat d'huissier a-t-il une valeur probante ?", a: "Le procès-verbal de constat dressé par un commissaire de justice a une force probante particulière en droit français. Il établit factuellement la situation observée à un instant donné." },
    ],
    related: ["trouver-un-avocat", "trouver-un-mediateur"],
  },
  "annuaire-experts": {
    label: "spécialiste référencé",
    plural: "spécialistes référencés",
    h1: "Réseau de spécialistes référencés CONFILEX",
    tagline: "5 professions réglementées · Sélection institutionnelle.",
    seo_title: "Réseau de spécialistes référencés CONFILEX",
    seo_description: "Réseau institutionnel : avocats, experts-comptables, notaires, médiateurs et commissaires de justice qualifiés. Orientation préalable par qualification documentaire.",
    profession_slug: null,
    intent_keywords: ["réseau de spécialistes", "réseau d'avocats spécialisés", "recherche avocats", "recherche notaires"],
    service_areas: [
      "Avocats", "Experts-comptables", "Notaires", "Médiateurs",
      "Commissaires de justice", "Conseillers en gestion de patrimoine",
    ],
    faq: [
      { q: "Comment CONFILEX référence-t-il les spécialistes ?", a: "Référencement après qualification documentaire et vérification d'inscription à un Ordre ou Barreau. Aucun classement public, aucune notation, aucune commission CONFILEX sur les honoraires des professionnels." },
      { q: "Comment accéder à un spécialiste ?", a: "L'accès passe par une qualification préalable du besoin via CONFILEX Match™ : la plateforme propose ensuite des profils dont la spécialisation correspond. Aucune liste librement consultable — la qualification documentaire est obligatoire." },
    ],
    related: ["trouver-un-avocat", "trouver-un-expert-comptable", "trouver-un-notaire"],
  },
};


export default function TrouverExpertPage({ slug }) {
  const { city } = useParams();
  const cfg = TROUVER_PAGES[slug];
  const [verticals, setVerticals] = useState([]);

  useEffect(() => {
    apiClient.get("/verifier/verticals")
      .then((r) => setVerticals(r.data.modules || []))
      .catch(() => {});
  }, []);

  if (!cfg) {
    return <div className="p-20 text-center">Page non trouvée.</div>;
  }

  const cityLabel = city
    ? city.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : null;
  const cityTitle = cityLabel
    ? `${cfg.h1} à ${cityLabel}`
    : cfg.h1;

  // Canonical : si city, on garde l'URL spécifique (PageRank propre)
  const canonical = city ? `/${slug}/${city}` : `/${slug}`;
  const finalTitle = cityLabel
    ? `${cfg.seo_title.replace("Orientation CONFILEX", "")} à ${cityLabel} — CONFILEX`.replace(/\s+/g, " ").trim()
    : cfg.seo_title;
  const finalDescription = cityLabel
    ? `Trouver un ${cfg.label} à ${cityLabel} avec CONFILEX : ${cfg.seo_description}`
    : cfg.seo_description;

  // JSON-LD Service (Schema.org)
  const serviceLD = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": cityTitle,
    "description": finalDescription,
    "provider": {
      "@type": "Organization",
      "name": "CONFILEX",
      "url": "https://confilex.fr",
    },
    "areaServed": cityLabel
      ? { "@type": "City", "name": cityLabel }
      : { "@type": "Country", "name": "France" },
    "serviceType": cfg.label,
    "termsOfService": "https://confilex.fr/legal",
  };

  return (
    <>
      <Seo
        title={finalTitle}
        description={finalDescription}
        canonical={canonical}
        keywords={[...cfg.intent_keywords, ...(cityLabel ? [`${cfg.label} ${cityLabel}`] : [])]}
        jsonLd={[
          serviceLD,
          ldBreadcrumb([
            { name: "Accueil", url: "/" },
            ...(cityLabel
              ? [
                  { name: cfg.h1, url: `/${slug}` },
                  { name: cityLabel, url: canonical },
                ]
              : [{ name: cfg.h1, url: canonical }]
            ),
          ]),
          ldFAQ(cfg.faq),
        ]}
      />

      {/* Hero */}
      <main className="bg-paper min-h-screen" data-testid={`trouver-${slug}-page`}>
        <section className="bg-night text-paper">
          <div className="max-w-5xl mx-auto px-6 py-16">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">
              CONFILEX · ORIENTATION INSTITUTIONNELLE{cityLabel && ` · ${cityLabel.toUpperCase()}`}
            </p>
            <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5" data-testid="page-h1">
              {cityTitle}
            </h1>
            <p className="text-paper/75 text-base max-w-3xl leading-relaxed mb-6">
              {cfg.tagline} CONFILEX qualifie d'abord votre dossier puis vous oriente vers des profils
              {city ? ` exerçant ${cityLabel ? `à ${cityLabel}` : "en France"}` : " sur tout le territoire"}.
            </p>
            <div className="flex gap-3 flex-wrap">
              <Link to="/orientation-intelligente">
                <Button className="bg-copper text-paper hover:bg-copper/90 rounded-sm" data-testid="cta-orientation">
                  <Compass className="w-4 h-4 mr-2" /> Démarrer l'orientation
                </Button>
              </Link>
              <Link to="/verifier">
                <Button variant="outline" className="border-paper/40 text-paper hover:bg-paper/10 rounded-sm" data-testid="cta-verifier">
                  <ScanLine className="w-4 h-4 mr-2" /> Faire vérifier mes documents
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Domaines couverts */}
        <section className="max-w-5xl mx-auto px-6 py-12">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">
            DOMAINES COUVERTS
          </p>
          <h2 className="font-serif italic text-3xl text-night mb-6">
            Pour quel type de dossier ?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {cfg.service_areas.map((area) => (
              <Card key={area} className="p-4 border-sage hover:border-copper transition-smooth" data-testid={`area-${area.toLowerCase().replace(/\s+/g, "-")}`}>
                <BookOpen className="w-4 h-4 text-copper mb-2" />
                <p className="text-sm font-medium text-night">{area}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Pourquoi CONFILEX */}
        <section className="bg-ivory border-t border-sage">
          <div className="max-w-5xl mx-auto px-6 py-14">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">
              POURQUOI CONFILEX
            </p>
            <h2 className="font-serif italic text-3xl text-night mb-8">
              Une orientation institutionnelle, jamais commerciale.
            </h2>
            <div className="grid sm:grid-cols-3 gap-5">
              <Card className="p-5 bg-paper border-sage">
                <BadgeCheck className="w-5 h-5 text-copper mb-3" />
                <h3 className="font-semibold text-night mb-2">Qualification documentaire préalable</h3>
                <p className="text-sm text-night/70 leading-relaxed">
                  Votre besoin est analysé avant toute mise en relation. Aucune liste consultable librement.
                </p>
              </Card>
              <Card className="p-5 bg-paper border-sage">
                <ShieldCheck className="w-5 h-5 text-copper mb-3" />
                <h3 className="font-semibold text-night mb-2">Indépendance déontologique</h3>
                <p className="text-sm text-night/70 leading-relaxed">
                  Aucun classement, aucune notation publique, aucune commission CONFILEX sur les honoraires
                  des professionnels.
                </p>
              </Card>
              <Card className="p-5 bg-paper border-sage">
                <Users className="w-5 h-5 text-copper mb-3" />
                <h3 className="font-semibold text-night mb-2">Réseau qualifié</h3>
                <p className="text-sm text-night/70 leading-relaxed">
                  {cfg.plural.charAt(0).toUpperCase() + cfg.plural.slice(1)} inscrits aux Ordres et Barreaux français en activité.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ structurée */}
        <section className="max-w-3xl mx-auto px-6 py-14" data-testid="page-faq">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">
            QUESTIONS FRÉQUENTES
          </p>
          <h2 className="font-serif italic text-3xl text-night mb-8">
            Questions sur la recherche d'un {cfg.label}
          </h2>
          <div className="space-y-5">
            {cfg.faq.map((item, i) => (
              <Card key={i} className="p-5 border-sage" data-testid={`faq-item-${i}`}>
                <h3 className="font-semibold text-night mb-2">{item.q}</h3>
                <p className="text-sm text-night/75 leading-relaxed">{item.a}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Maillage interne — pages liées */}
        <section className="bg-night text-paper">
          <div className="max-w-5xl mx-auto px-6 py-12">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">
              EXPLORER LE RÉSEAU CONFILEX
            </p>
            <h2 className="font-serif italic text-3xl mb-6">Autres orientations possibles</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cfg.related.map((rel) => {
                const target = TROUVER_PAGES[rel];
                if (!target) return null;
                return (
                  <Link
                    key={rel}
                    to={`/${rel}`}
                    className="block p-4 border border-paper/20 rounded-sm hover:border-copper hover:bg-paper/5 transition-smooth"
                    data-testid={`related-${rel}`}
                  >
                    <Users className="w-4 h-4 text-copper mb-2" />
                    <p className="text-sm font-medium">{target.h1}</p>
                    <p className="text-[11px] text-paper/55 mt-1">{target.tagline}</p>
                  </Link>
                );
              })}
              <Link
                to="/verifier"
                className="block p-4 border border-paper/20 rounded-sm hover:border-copper hover:bg-paper/5 transition-smooth"
                data-testid="related-verifier"
              >
                <ScanLine className="w-4 h-4 text-copper mb-2" />
                <p className="text-sm font-medium">Audit Documentaire Intelligent</p>
                <p className="text-[11px] text-paper/55 mt-1">Lire et structurer vos pièces avant toute consultation.</p>
              </Link>
              <Link
                to="/audit/transverse"
                className="block p-4 border border-paper/20 rounded-sm hover:border-copper hover:bg-paper/5 transition-smooth"
                data-testid="related-transverse"
              >
                <GitMerge className="w-4 h-4 text-copper mb-2" />
                <p className="text-sm font-medium">Audit transverse multi-pièces</p>
                <p className="text-[11px] text-paper/55 mt-1">Croiser plusieurs documents du même dossier.</p>
              </Link>
              <Link
                to="/orientation-intelligente"
                className="block p-4 border border-paper/20 rounded-sm hover:border-copper hover:bg-paper/5 transition-smooth"
                data-testid="related-orientation"
              >
                <Compass className="w-4 h-4 text-copper mb-2" />
                <p className="text-sm font-medium">Orientation intelligente</p>
                <p className="text-[11px] text-paper/55 mt-1">Qualifier votre dossier avec l'IA d'orientation CONFILEX.</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Compliance footer */}
        <section className="border-t border-sage bg-ivory">
          <div className="max-w-5xl mx-auto px-6 py-8 flex items-start gap-3 text-sm text-night/75">
            <ShieldCheck className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
            <p>
              CONFILEX n'est pas un cabinet d'avocats, d'experts-comptables, de notaires ni
              de conseil fiscal. Aucune analyse engageant une responsabilité professionnelle
              n'est produite par la plateforme. Les professionnels référencés exercent en
              toute indépendance et conservent l'entière responsabilité de leur analyse et
              de leurs recommandations.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}
